import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Redirect } from "react-router-dom"
import { Alert, Button, Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap"
import { MetaTags } from "react-meta-tags"
import BootstrapTable from "react-bootstrap-table-next"
import { AppConfiguration } from "config"
import { customSortCaret } from "helpers/bootstrap_table_helper"
import { getAppropriateLoadingGif } from "helpers/sf_utils"
import { REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants"
import useFeatureFlags from "hooks/useFeatureFlags"
import SfLoadingSkeleton from "components/Common/Loadings/SfLoadingSkeleton"
import OffsetPagination from "components/Common/OffsetPagination"
import AdminUserProfileBreadCrumb from "../Authentication/AdminUserProfileBreadCrumb"
import MiloCampaignDrawer from "./MiloCampaignDrawer"
import { MILO_LOAD_ERROR, fetchMiloCampaigns } from "./miloApi"
import {
    MILO_DEFAULT_SORT,
    MILO_FULL_NAME,
    MILO_REASON_OPTIONS,
    MILO_STATUS_OPTIONS,
    canSeeMilo,
    miloCampaignColumns,
} from "./milo_helper"
import "./milo.scss"

// Must stay a module-level constant: a new object each render makes the table re-emit sort events.
const REMOTE_SORT = { sort: true }
// Wait for typing to stop before filtering by account: each list call is a heavy reporting query.
const ACCOUNT_ID_DEBOUNCE_MS = 400
const EMPTY_FILTERS = { status: "", reason_code: "", behind_pace: false, account_id: "" }

// d-flex + flex-fill keeps the four cards the same height when a hint wraps to two lines.
const KpiCard = ({ label, value, icon, tone = "primary", hint }) => (
    <Col xl={3} sm={6} className="d-flex">
        <Card className="mini-stats-wid flex-fill">
            <CardBody className="d-flex align-items-start">
                <div className="flex-grow-1">
                    <p className="text-muted fw-medium mb-1">{label}</p>
                    <div className="milo-kpi-value">{value ?? "—"}</div>
                    {hint && <div className="text-muted font-size-12">{hint}</div>}
                </div>
                <div className={`avatar-sm flex-shrink-0 ms-3 rounded-circle bg-${tone} bg-soft d-flex align-items-center justify-content-center`}>
                    <i className={`bx ${icon} font-size-22 text-${tone}`} />
                </div>
            </CardBody>
        </Card>
    </Col>
)

const MiloDashboard = () => {
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [accountIdInput, setAccountIdInput] = useState("")
    const [sortState, setSortState] = useState(MILO_DEFAULT_SORT)
    const [pageNumber, setPageNumber] = useState(1)
    const [response, setResponse] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [reloadKey, setReloadKey] = useState(0)
    const [openCampaign, setOpenCampaign] = useState(null)

    // One owner for the list fetch; a stale response is dropped if filters change mid-request.
    useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError(null)
        fetchMiloCampaigns({ ...filters, ...sortState, page_number: pageNumber })
            .then(body => !cancelled && setResponse(body))
            .catch(e => !cancelled && setError(e.message || MILO_LOAD_ERROR))
            .finally(() => !cancelled && setLoading(false))
        return () => { cancelled = true }
    }, [filters, sortState, pageNumber, reloadKey])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (accountIdInput !== filters.account_id) updateFilter("account_id", accountIdInput)
        }, ACCOUNT_ID_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [accountIdInput])

    const columns = useMemo(() => miloCampaignColumns(setOpenCampaign), [])
    const defaultSorted = useMemo(() => [{
        dataField: sortState.sort_column,
        order: sortState.sort_direction,
    }], [sortState])

    const updateFilter = (key, value) => {
        setPageNumber(1)
        setFilters(prev => ({ ...prev, [key]: value }))
    }

    const handleTableChange = (type, { sortField, sortOrder }) => {
        if (type !== "sort") return
        if (sortField === sortState.sort_column && sortOrder === sortState.sort_direction) return
        setPageNumber(1)
        setSortState({ sort_column: sortField, sort_direction: sortOrder })
    }

    const rows = response?.data || []
    const meta = response?.additional_data || {}
    const summary = meta.summary || {}
    const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS)
    // The API sends the exact page count unless more pages exist than its lookahead window covers.
    const lookaheadPages = meta.lookahead_pages || 1
    const totalPages = meta.total_number_of_pages ||
        (meta.has_next_page ? pageNumber + (meta.has_lookahead_pages ? lookaheadPages : 1) : pageNumber)

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>MILO | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <AdminUserProfileBreadCrumb title="Admin" breadcrumbItem="MILO" />
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <span className="text-muted">{MILO_FULL_NAME}</span>
                        <span className="badge badge-soft-warning font-size-12">Shadow mode — suggestions are not applied</span>
                    </div>

                    <Alert color="info" className="py-2 font-size-12">
                        <i className="bx bx-info-circle me-1" />
                        Campaigns and decision history are live. The <b>Charts</b> tab in the side panel still shows
                        sample data until the MILO metrics API is built.
                    </Alert>

                    <Row>
                        <KpiCard label="Campaigns tracked" value={summary.campaigns_tracked} icon="bx-target-lock" hint="Goal lead campaigns with a daily cap" />
                        <KpiCard label="Behind pace" value={summary.behind_pace} icon="bx-trending-down" tone="danger" hint="Pace below 0.90 right now" />
                        <KpiCard label="Raise suggested" value={summary.raise_suggested} icon="bx-trending-up" tone="success" hint="Latest suggestion above no change" />
                        <KpiCard label="Failed or rejected" value={summary.failed_or_rejected_24h} icon="bx-error" tone="warning" hint="Last 24 hours" />
                    </Row>

                    <Card>
                        <CardBody>
                            <Row className="g-2 align-items-end mb-3">
                                <Col md={3}>
                                    <Label className="font-size-12 text-muted mb-1">Status</Label>
                                    <Input type="select" value={filters.status} onChange={e => updateFilter("status", e.target.value)}>
                                        {MILO_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </Input>
                                </Col>
                                <Col md={3}>
                                    <Label className="font-size-12 text-muted mb-1">Reason</Label>
                                    <Input type="select" value={filters.reason_code} onChange={e => updateFilter("reason_code", e.target.value)}>
                                        {MILO_REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </Input>
                                </Col>
                                <Col md={2}>
                                    <Label className="font-size-12 text-muted mb-1">Account ID</Label>
                                    {/* Digits only: the API matches the account ID exactly. */}
                                    <Input type="search" inputMode="numeric" placeholder="e.g. 51001" value={accountIdInput} onChange={e => setAccountIdInput(e.target.value.replace(/\D/g, ""))} />
                                </Col>
                                <Col md="auto" className="pb-2">
                                    <div className="form-check form-switch">
                                        <Input type="checkbox" className="form-check-input" id="milo-behind-pace" checked={filters.behind_pace} onChange={e => updateFilter("behind_pace", e.target.checked)} />
                                        <Label className="form-check-label" for="milo-behind-pace">Behind pace only</Label>
                                    </div>
                                </Col>
                                <Col md="auto" className="ms-auto">
                                    {hasActiveFilters && (
                                        <Button color="link" onClick={() => { setPageNumber(1); setAccountIdInput(""); setFilters(EMPTY_FILTERS) }}>Clear filters</Button>
                                    )}
                                </Col>
                            </Row>

                            {error ? (
                                <div className="text-center py-5">
                                    <i className="bx bx-error-circle font-size-24 text-danger d-block mb-2" />
                                    <p className="mb-2">{error}</p>
                                    <Button color="primary" outline size="sm" onClick={() => setReloadKey(k => k + 1)}>Try again</Button>
                                </div>
                            ) : (
                                <div className="sfTable-Container table-responsive" style={{ opacity: loading && rows.length ? 0.6 : 1 }}>
                                    {(loading && !rows.length) ? <SfLoadingSkeleton rows={8} cols={8} /> : (
                                        <BootstrapTable
                                            keyField="campaign_id"
                                            columns={columns}
                                            data={rows}
                                            bordered={false}
                                            rowClasses="tr-hover"
                                            headerClasses="sftableHeader text-black thead-h50"
                                            classes="table align-middle table-nowrap text-black"
                                            headerWrapperClasses="thead-light"
                                            noDataIndication={hasActiveFilters
                                                ? "No campaigns match these filters."
                                                : "No MILO data yet. Campaigns appear here after their first check (Goal lead campaigns with a daily cap)."}
                                            sort={{ sortCaret: customSortCaret }}
                                            defaultSorted={defaultSorted}
                                            remote={REMOTE_SORT}
                                            onTableChange={handleTableChange}
                                        />
                                    )}
                                </div>
                            )}

                            {!error && (
                                <OffsetPagination
                                    currentPage={pageNumber}
                                    totalPages={totalPages}
                                    hasNextPage={meta.has_next_page}
                                    hasDeepOffsetPages={meta.has_lookahead_pages}
                                    offsetLookAheadPages={lookaheadPages}
                                    loading={loading}
                                    className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                    onPageChange={(p) => {
                                        const parsed = Number(p)
                                        if (Number.isFinite(parsed) && parsed >= 1) setPageNumber(parsed)
                                    }}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
            <MiloCampaignDrawer campaign={openCampaign} onClose={() => setOpenCampaign(null)} />
        </React.Fragment>
    )
}

// The flag and role checks run before MiloDashboard mounts, so a blocked admin never sends a MILO
// request. The sidebar only hides the link; this also covers typing /milo with the flag off.
// On a direct open the admin and the flags are still loading, so wait for both before deciding.
const MiloPage = () => {
    const currentAdminUser = useSelector(state => state.LoginAdminUser.current_user)
    const [isFeatureFlagActive, areFeatureFlagsLoaded] = useFeatureFlags()
    if (!currentAdminUser || !areFeatureFlagsLoaded) return getAppropriateLoadingGif(AppConfiguration?.PRODUCT_NAME)
    if (!isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_MILO_DASHBOARD) || !canSeeMilo(currentAdminUser)) {
        return <Redirect to="/forbidden" />
    }
    return <MiloDashboard />
}

export default MiloPage
